<script setup>
import { debounce, isNull, isUndefined } from 'lodash'
import { computed, ref, provide, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import DatePicker from 'vue2-datepicker'

import ObjectivesApiHandler from '@/api/okr-elements'
import { useBackDate } from '@/utils/backdate'
import {
  CHECKIN_FIELDS,
  CHECKIN_FIELDS_IDS,
  CHECKIN_FIELDS_KEYS,
  useCheckinTemplates,
  useCheckinValueField
} from '@/utils/check-ins/check-ins'
import { FIELD_VARIANTS } from '@/utils/components-configurations/checkin-field'
import { ACTION_TYPES } from '@/utils/components-configurations/chekin-author-actions'
import { datepickerLang, UNSELECTED_DATE } from '@/utils/date'
import {
  ASSIGNEE_ENTITY_KEYS,
  CHECKIN_ENTITY_KEYS,
  CHECKIN_TEMPLATE_ENTITY_KEYS
} from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { commentFormInjectionKey } from '@/utils/injection-keys'
import {
  calculateObjectiveStatus,
  findStatusOptionByValue,
  OKR_STATUSES,
  OKR_STATUSES_DISABLED
} from '@/utils/objectives'
import { getGradeDisplayValue } from '@/utils/okr-elements/grade'
import { numberOrNullProp } from '@/utils/prop-validators'
import { DESCRIPTION_FILED_LIMITS } from '@/utils/rich-edit'
import { COMMENT_FIELD_PLACEMENTS } from '@/utils/tiptap-utils'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

import CheckinTemplateModal from '@/components/check-ins/CheckinTemplateModal'
import {
  CHECKIN_COMMENT_FIELD_TEST_ID,
  CHECKIN_DATEPICKER_TEST_ID
} from '@/components/check-ins/jest-helpers'
import OkrGradeValue from '@/components/form/OkrGradeValue'
import CheckinAuthorActions from '@/components/objectives/element-check-in/CheckinAuthorActions'
import CheckinLayout from '@/components/objectives/element-check-in/CheckinLayout'
import CheckinValuesField from '@/components/objectives/element-check-in/CheckinValuesField'
import CheckinField from '@/components/objectives/element-check-in/ChekinField'
import DescriptionField from '@/components/objectives/forms/DescriptionField'
import DescriptionFieldTemplatesSelect from '@/components/objectives/forms/DescriptionFieldTemplatesSelect'
import StatusSelect from '@/components/objectives/status-select/StatusSelect'
import AnimatedNumber from '@/components/ui/AnimatedNumber/AnimatedNumber'
import AppCheckbox from '@/components/ui/AppCheckbox/AppCheckbox'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import FormStatusButton from '@/components/ui/AppSelect/TriggerButtons/FormStatusButton'

defineOptions({
  name: 'CheckinForm'
})

const model = defineModel({
  type: Object,
  required: true
})

const isProgressLoadingModel = defineModel('isProgressLoading', {
  type: Boolean,
  required: true
})

const isCommentTooLongModel = defineModel('isCommentTooLong', {
  type: Boolean,
  required: true
})

const props = defineProps({
  presetBackdate: {
    type: [String, Date, Number],
    default: null
  },

  okrElement: {
    type: Object,
    required: true
  },

  isShowCloseNestedElementsCheckbox: {
    type: Boolean
  },

  isEdit: {
    type: Boolean
  },

  isLaunchAsReopen: {
    type: Boolean
  },

  tableActionsAppendTo: {
    type: String,
    default: ''
  },

  overrideCurrentValue: {
    validator: v => numberOrNullProp(v) || isUndefined(v),
    default: null
  },

  initDescriptionField: {
    type: Boolean
  }
})

const { t } = useI18n()

const {
  ID,
  BACKDATE,
  LOCAL_CONFIDENCE_LEVEL_ID,
  LOCAL_AUTOMATIC_CONFIDENCE_LEVEL_ID,
  COLOR,
  VALUE,
  LOCAL_GRADE_VALUE,
  LOCAL_CURRENT_VALUE,
  CLOSE_NESTED_ELEMENTS,
  COMMENT,
  TOOLTIP
} = CHECKIN_ENTITY_KEYS

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ BACKDATE SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

const { notAfterToday, isTodaySelected, DATE_FORMAT, getBackdateAsRequestParameter } = useBackDate({
  selectedDate: () => model.value[BACKDATE]
})

const isDatePickerDisabled = computed(() => {
  return (
    !isNull(props.presetBackdate) ||
    OKR_STATUSES_DISABLED.includes(model.value[LOCAL_CONFIDENCE_LEVEL_ID])
  )
})

const calendarPlaceholder = computed(() => {
  return isTodaySelected.value ? t('filter.today') : UNSELECTED_DATE
})

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ BACKDATE SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ COMMENT FORM SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮
const commentFormIsActive = ref(false)

const commentFormData = ref({
  opened: undefined // undefined if no comment form is opened and uid of component if it is
})

provide(commentFormInjectionKey, commentFormData)

// const commentReference = useTemplateRef('comment-reference')
// const commentFieldReference = useTemplateRef('comment-field-reference')
//
// onClickOutside(commentReference, () => {
//   if (commentFormIsActive.value) {
//     commentFieldReference.value.onCancel()
//   }
// })

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ COMMENT FORM SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ CHECKIN FORM SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

const { hasContributeChildren, isValueFieldNeeded } = useCheckinValueField({
  okrElement: () => props.okrElement
})

const checkinFields = computed(() => {
  if (props.isLaunchAsReopen) {
    return []
  }
  const fields = [
    {
      ...CHECKIN_FIELDS[CHECKIN_FIELDS_KEYS.PROGRESS_CHANGE],
      [TOOLTIP]: isValueFieldNeeded.value ? null : t('update.keyResult.result_type_lock_message')
    },

    {
      ...CHECKIN_FIELDS[CHECKIN_FIELDS_KEYS.STATUS_CHANGE]
    }
  ]

  if (isValueFieldNeeded.value) {
    fields.unshift({
      ...CHECKIN_FIELDS[CHECKIN_FIELDS_KEYS.VALUE_CHANGE]
    })
  }

  return fields
})

const progressColor = computed(() => {
  const { confidenceLevelId, automaticConfidenceLevelId } = objectiveForStatus.value

  const resolvedConfidenceLevelId = calculateObjectiveStatus(
    confidenceLevelId,
    automaticConfidenceLevelId
  )

  return findStatusOptionByValue({
    [VALUE]: resolvedConfidenceLevelId
  })[COLOR]
})
// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ CHECKIN FORM SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ GRADE AND STATUS SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

const localGradeDisplayValue = computed(() => {
  return getGradeDisplayValue(model.value[LOCAL_GRADE_VALUE])
})

const gradeForConfidenceLevel = computed(() => {
  return Number(model.value[LOCAL_GRADE_VALUE])
})

const emit = defineEmits({
  'confidence-level-id-updated': null
})

const onUpdateLocalConfidenceLevelId = confidenceLevelId => {
  model.value[LOCAL_CONFIDENCE_LEVEL_ID] = confidenceLevelId
  emit('confidence-level-id-updated')
  if (confidenceLevelId === OKR_STATUSES.AUTO) {
    debouncedGetActualGradeValue({
      [VALUE]: model.value[LOCAL_CURRENT_VALUE]
    })
  }
}

const objectiveForStatus = computed(() => {
  return {
    ...props.okrElement,
    confidenceLevelId: model.value[LOCAL_CONFIDENCE_LEVEL_ID],
    automaticConfidenceLevelId: model.value[LOCAL_AUTOMATIC_CONFIDENCE_LEVEL_ID]
  }
})

const unavailableStatuses = computed(() => {
  if (!isTodaySelected.value || props.isEdit) {
    return [...OKR_STATUSES_DISABLED]
  }

  return []
})

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ GRADE AND STATUS SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ ACTUAL PROGRESS SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮
const gradeValueTriggers = computed(() => {
  return [model.value[LOCAL_CURRENT_VALUE], model.value[BACKDATE]]
})

const setActualProgress = async ({ value = 0 } = {}) => {
  if (hasContributeChildren.value) return

  const objectivesApi = new ObjectivesApiHandler()

  isProgressLoadingModel.value = true

  try {
    const { grade, autoConfidenceLevelId } = await objectivesApi.calculateProgressForElement({
      elementId: props.okrElement.id,
      [VALUE]: Number(value),
      date: getBackdateAsRequestParameter.value
    })

    model.value[LOCAL_GRADE_VALUE] = grade
    model.value[LOCAL_AUTOMATIC_CONFIDENCE_LEVEL_ID] = autoConfidenceLevelId || OKR_STATUSES.AUTO

    if (model.value[LOCAL_CONFIDENCE_LEVEL_ID] === OKR_STATUSES.NOT_STARTED) {
      model.value[LOCAL_CONFIDENCE_LEVEL_ID] = OKR_STATUSES.AUTO
    }
  } catch (error) {
    handleError({ error })
  } finally {
    isProgressLoadingModel.value = false
  }
}
const debouncedGetActualGradeValue = debounce(
  ({ value = 0 } = {}) => setActualProgress({ value }),
  500
)

watch(
  gradeValueTriggers,
  newValue => {
    const [localCurrentValue] = newValue

    debouncedGetActualGradeValue({
      [VALUE]: localCurrentValue
    })
  },
  { deep: true }
)

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ ACTUAL PROGRESS SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

onMounted(() => {
  if (!isNull(props.overrideCurrentValue) && !isUndefined(props.overrideCurrentValue)) {
    model.value[LOCAL_CURRENT_VALUE] = props.overrideCurrentValue
  }
})

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ TEMPLATES SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮

const {
  bottomFixedItems,
  optionsList,
  onTemplateSelect,
  descriptionFieldReferenceKey,
  templateModalState,
  fetchTemplates,
  closeTemplateModal
} = useCheckinTemplates({
  okrElement: () => props.okrElement,
  checkinCommentContent: () => model.value[COMMENT]
})

// ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮ TEMPLATES SCOPE ▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮
</script>

<template>
  <CheckinLayout
    :class="{
      'chf-Form-edit': isEdit
    }"
  >
    <template #author>
      <CheckinAuthorActions
        :account-id-key="ASSIGNEE_ENTITY_KEYS.USER_ACCOUNT_ID"
        :action-type="ACTION_TYPES.CREATE"
        :avatar-url-key="ASSIGNEE_ENTITY_KEYS.USER_AVATAR_URL"
        :user-name-key="ASSIGNEE_ENTITY_KEYS.USER_NAME"
      >
        <template #date>
          <DatePicker
            v-model="model[BACKDATE]"
            :append-to-body="true"
            :class="{
              'chf-Datepicker-disabled': isDatePickerDisabled
            }"
            :clearable="false"
            :data-auto-testid="CHECKIN_DATEPICKER_TEST_ID"
            :data-testid="CHECKIN_DATEPICKER_TEST_ID"
            :disabled="isDatePickerDisabled"
            :disabled-date="notAfterToday"
            :editable="false"
            :format="DATE_FORMAT"
            :lang="datepickerLang()"
            class="chf-Datepicker"
            popup-class="dp-Datepicker"
          >
            <template #input="{ class: className, value }">
              <div
                :class="{
                  [className]: true
                }"
                class="chf-Datepicker_Input"
              >
                {{ calendarPlaceholder || value }}
              </div>
            </template>
            <template #icon-calendar>
              <AppIcon
                class="chf-Datepicker_Chevron"
                height="24"
                icon-name="arrow-down-black-next"
                width="24"
              />
            </template>
          </DatePicker>
        </template>
      </CheckinAuthorActions>
    </template>

    <template #body>
      <CheckinField
        v-for="field in checkinFields"
        :key="field[ID]"
        :field
        :label-key="CHECKIN_ENTITY_KEYS.CREATE_LABEL"
        :variant="field[ID] === CHECKIN_FIELDS_IDS.VALUE ? FIELD_VARIANTS.COLUMN : undefined"
      >
        <template #field-value="{ field: fieldProps }">
          <CheckinValuesField
            v-if="fieldProps[ID] === CHECKIN_FIELDS_IDS.VALUE"
            v-model:current-value="model[LOCAL_CURRENT_VALUE]"
            :okr-element
            :selected-date="model[BACKDATE]"
          />

          <AnimatedNumber
            v-if="fieldProps[ID] === CHECKIN_FIELDS_IDS.PROGRESS"
            v-slot="{ displayValue }"
            :end-amount="localGradeDisplayValue"
            skip-initial-animation
          >
            <OkrGradeValue
              :grade="Number(displayValue)"
              :style="{ color: progressColor }"
              class="chf-Progress"
              data-auto-testid="grade"
            />
          </AnimatedNumber>

          <StatusSelect
            v-if="fieldProps[ID] === CHECKIN_FIELDS_IDS.STATUS"
            :grade="gradeForConfidenceLevel"
            :objective="objectiveForStatus"
            :unavailable-statuses="unavailableStatuses"
            autocalculate-toggle
            data-auto-testid="status-switch"
            placement="bottom-end"
            @update:confidence-level-id="onUpdateLocalConfidenceLevelId"
          >
            <template #button="{ option, opened, disabled, basedOnGrade }">
              <FormStatusButton
                v-if="option"
                :based-on-grade="basedOnGrade"
                :disabled="disabled"
                :opened="opened"
                :option="option"
              />
            </template>
          </StatusSelect>
        </template>

        <template
          v-if="field[ID] === CHECKIN_FIELDS_IDS.STATUS && isShowCloseNestedElementsCheckbox"
          #field-footer
        >
          <AppCheckbox
            v-model="model[CLOSE_NESTED_ELEMENTS]"
            style="--label-color: var(--dark-1); --checkmark-margin: 4px"
          >
            {{ t('element.close_nested_description') }}
          </AppCheckbox>
        </template>
      </CheckinField>

      <div
        :class="{
          'chf-BodyFields_Comment-active': commentFormIsActive
        }"
        class="chf-BodyFields_Comment"
      >
        <DescriptionField
          :ref="descriptionFieldReferenceKey"
          v-model="model[COMMENT]"
          :actions="false"
          :data-auto-testid="CHECKIN_COMMENT_FIELD_TEST_ID"
          :data-testid="CHECKIN_COMMENT_FIELD_TEST_ID"
          :max-length="DESCRIPTION_FILED_LIMITS.COMMENT"
          :placeholder="t('checkin.comment_placeholder')"
          :placement="COMMENT_FIELD_PLACEMENTS"
          :table-actions-append-to
          :workspace-id="props.okrElement[OKR_ELEMENT_KEYS.WORKSPACE_ID]"
          :show-on-init="initDescriptionField"
          class="chf-CommentField"
          hard-reset-on-empty
          short-name-text-format
          simple-view
          @active="commentFormIsActive = $event"
          @characters-limit-exceed="isCommentTooLongModel = $event"
        >
          <template #toolbar-append>
            <div class="chf-TemplatesSelect">
              <DescriptionFieldTemplatesSelect
                :bottom-fixed-items="bottomFixedItems"
                :item-label="CHECKIN_TEMPLATE_ENTITY_KEYS.TITLE"
                :item-value="CHECKIN_TEMPLATE_ENTITY_KEYS.ID"
                :options="optionsList"
                @select-template="onTemplateSelect"
              />
            </div>
          </template>
        </DescriptionField>
      </div>
      <portal to="modal-windows">
        <CheckinTemplateModal
          :show="templateModalState.isShow"
          :template-content="templateModalState.templateContent"
          @template-saved="fetchTemplates"
          @on-close="closeTemplateModal"
        />
      </portal>
    </template>
  </CheckinLayout>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';
@import '~@/assets/styles/interval-modal';

.chf-Progress {
  --value-font-size: #{$fs-14};
  --suffix-font-size: #{$fs-14};
  --suffix-font-weight: #{fw('bold')};
  --value-max-width: 10ch;
  --value-font-weight: #{fw('bold')};
  --value-line-height: 20px;
}

.chf-CommentField {
  width: 100%;
}

.chf-BodyFields_Comment {
  --description-field-margin-bottom: 0;
  --editor-active-outer-padding: 8px;
  --placeholder-hover-color: none;
  --placeholder-padding: 12px 10px;
}

.chf-BodyFields_Comment {
  width: var(--checkin-field-width);

  &:not(&-active) {
    .chf-Form-edit & {
      margin-top: 12px;
    }
  }
}

.chf-CommentPlaceholder {
  color: $dark-1;
  font-size: $fs-14;
  line-height: 16px;
  font-weight: fw('semi-bold');
}

.chf-Datepicker {
  @extend %interval-datepicker-styles;
  width: auto;
  flex-shrink: 0;

  :deep(.mx-icon-calendar) {
    right: 0;
  }
}

.chf-Datepicker_Input {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: fw('semi-bold');
  --datepicker-input-padding: 0px 24px 0px 0px;
  border: none;
  height: 24px;
}

.chf-Datepicker_Input,
.chf-Datepicker_Chevron {
  color: $primary-color-next;

  .chf-Datepicker-disabled & {
    cursor: not-allowed;
    color: $grey-1-next;
  }
}

.chf-TemplatesSelect {
  margin-left: auto;
}
</style>
